import { getAuthErrorMessage, normalizeAuthErrorCode, readRawAuthErrorFromLocation, stripAuthErrorFromUrl } from "./auth-fe11.esm.js";
import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, GoogleIcon, Text } from "@opendoor/bricks-next";
import { globalObservability } from "@opendoor/observability/slim";
//#region src/google/GoogleAuthRedirectButton.tsx
var GOOGLE_AUTH_METHOD = 3;
var WEB_AUTH_SOURCE = 0;
var WEB_PLATFORM = 1;
/**
* Redirect-based Google auth button for the server-side OAuth flow.
* Navigates to /auth/google — Rails handles the full OAuth exchange via OmniAuth.
* No Google JavaScript SDK in the login path.
*/
var GoogleAuthRedirectButton = ({ analyticsPrefix, redirectTo, redirectIntent, label = "Continue with Google", size = "medium", variant = "secondary", fullWidth = false, onBeforeNavigate, onBeforeNavigateError }) => {
	const handlePress = useGoogleRedirectHandler({
		redirectTo,
		redirectIntent,
		onBeforeNavigate,
		onBeforeNavigateError
	});
	const authErrorCode = useAuthErrorOnMount();
	const iconSize = size === "xsmall" || size === "small" ? 20 : 24;
	return /* @__PURE__ */ React.createElement(Box, {
		width: "100%",
		maxWidth: fullWidth ? void 0 : 400
	}, /* @__PURE__ */ React.createElement(Box, { position: "relative" }, /* @__PURE__ */ React.createElement(Button, {
		analyticsName: `${analyticsPrefix}-google-redirect-button`,
		testID: "google-auth-redirect-button",
		label,
		size,
		variant,
		onPress: handlePress,
		...fullWidth ? { width: "100%" } : {}
	}), /* @__PURE__ */ React.createElement(Box, {
		position: "absolute",
		left: "$12x",
		top: "50%",
		style: { transform: [{ translateY: "-50%" }] },
		pointerEvents: "none"
	}, /* @__PURE__ */ React.createElement(GoogleIcon, { size: iconSize }))), authErrorCode ? /* @__PURE__ */ React.createElement(Box, {
		paddingTop: "$4x",
		role: "alert",
		"aria-live": "polite",
		testID: "google-auth-error-message"
	}, /* @__PURE__ */ React.createElement(Text, {
		tag: "span",
		typography: "$bodySmall",
		color: "$contentStateErrorDefault"
	}, getAuthErrorMessage(authErrorCode))) : null);
};
/** Returns the path if it's a safe relative path, or undefined if it's absolute/protocol-relative/scheme-based. */
function sanitizeRedirectPath(path) {
	if (!path) return void 0;
	if (/^\/(?!\/)/.test(path)) return path;
	console.error(`[GoogleAuthRedirectButton] rejected non-relative redirect path: ${path}`);
}
function navigateToGoogleAuth(redirectTo, redirectIntent) {
	const url = new URL("/auth/google", window.location.origin);
	const currentParams = new URLSearchParams(window.location.search);
	const redirectFromParams = currentParams.get("redirect_to");
	currentParams.delete("redirect_to");
	currentParams.forEach((value, key) => {
		url.searchParams.set(key, value);
	});
	const preservedSearch = currentParams.toString();
	const reauthDefaultRedirect = redirectIntent === "reauth" ? `${window.location.pathname}${preservedSearch ? `?${preservedSearch}` : ""}` : void 0;
	const safeRedirect = redirectIntent === "reauth" ? sanitizeRedirectPath(redirectTo) ?? sanitizeRedirectPath(reauthDefaultRedirect) : sanitizeRedirectPath(redirectTo) ?? sanitizeRedirectPath(redirectFromParams);
	if (safeRedirect) url.searchParams.set("redirect_to", safeRedirect);
	if (redirectIntent) url.searchParams.set("redirect_intent", redirectIntent);
	window.location.href = url.pathname + url.search;
}
function trackGoogleRedirectInitiated() {
	globalObservability.trackJourneyEvent("authentication", "redirectInitiated", {
		authMethod: GOOGLE_AUTH_METHOD,
		authSource: WEB_AUTH_SOURCE,
		platform: WEB_PLATFORM
	}, void 0, { flush: true });
}
function reportRedirectInitiatedTrackingError(error) {
	try {
		globalObservability.trackError("google-auth-redirect-initiated-journey-event-failed", error instanceof Error ? error : new Error(String(error)));
	} catch {}
}
function useGoogleRedirectHandler({ redirectTo, redirectIntent, onBeforeNavigate, onBeforeNavigateError }) {
	return useCallback(async () => {
		try {
			trackGoogleRedirectInitiated();
		} catch (e) {
			reportRedirectInitiatedTrackingError(e);
		}
		try {
			await onBeforeNavigate?.();
		} catch (e) {
			await onBeforeNavigateError?.(e);
		}
		globalObservability.getJourneyEventClient().send?.();
		navigateToGoogleAuth(redirectTo, redirectIntent);
	}, [
		redirectTo,
		redirectIntent,
		onBeforeNavigate,
		onBeforeNavigateError
	]);
}
/**
* Reads `?auth_error=` once on mount, fires telemetry, strips the param. Returns
* the normalized code for inline rendering, or null when no error is present.
*/
function useAuthErrorOnMount() {
	const [code, setCode] = useState(null);
	useEffect(() => {
		const raw = readRawAuthErrorFromLocation();
		if (raw === null) return;
		const normalized = normalizeAuthErrorCode(raw);
		setCode(normalized);
		stripAuthErrorFromUrl();
		reportAuthErrorDisplayed(normalized, raw);
	}, []);
	return code;
}
var AUTH_ERROR_DISPLAYED_MESSAGE = "Google OAuth redirect error displayed";
var SAFE_RAW_AUTH_ERROR_PATTERN = /^[a-z0-9_-]{1,64}$/;
function reportAuthErrorDisplayed(normalized, raw) {
	try {
		const data = {
			authMethod: GOOGLE_AUTH_METHOD,
			platform: WEB_PLATFORM,
			oauthErrorCode: normalized
		};
		if (raw !== normalized) {
			const trimmedRaw = raw.trim().toLowerCase();
			if (SAFE_RAW_AUTH_ERROR_PATTERN.test(trimmedRaw)) data.oauthErrorRawCode = trimmedRaw;
		}
		globalObservability.trackError("google-auth-redirect-error-displayed", new Error(AUTH_ERROR_DISPLAYED_MESSAGE), { data });
	} catch {}
}
//#endregion
export { GoogleAuthRedirectButton, sanitizeRedirectPath };

